<script setup lang="ts">
import {computed, ref, nextTick, inject} from "vue";
import {useFormDataStore} from "@/stores/formDataStore";
import MeteringPointDataTable from "@/components/MeteringPointDataTable.vue";
import { DialogConfirm } from '@es-bill/es-bill-common-components';
import { useI18n } from "vue-i18n";
import type { FormValidation } from '@/types/form-validation';
import type { FormEnergyCommunity } from '@/stores/communityApiStore';

const formDataStore = useFormDataStore();
const formValidation = inject('$formValidation') as FormValidation;
const { t } = useI18n();
const props = defineProps<{
  communityPublic?: FormEnergyCommunity;
}>()

import { isMultiDso } from '@/lib/utils/communityFlags';
const multiDsoMode = computed(()=> isMultiDso(props.communityPublic?.communityType));

const formValid = ref(false);
const theForm = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);
const isNoMeteringPointsDialogVisible = ref(false);

// Create a method to show the dialog and return a promise that resolves based on the user's choice
function showNoMeteringPointsDialog() {
  return new Promise((resolve) => {
    if (formDataStore.formData.meteringPointsAssociated.length === 0) {
      // Only show dialog if zero metering points are allowed by the community
      if (props.communityPublic?.allow0MeteringPoints) {
        isNoMeteringPointsDialogVisible.value = true;
        // Store the original resolvers
        dialogResolve = resolve;
      } else {
        // If not allowed, automatically add a metering point and continue
        formDataStore.addMeteringPoint();
        resolve(false); // Don't proceed yet, let user fill in the required metering point
      }
    } else {
      resolve(true); // No dialog needed, continue
    }
  });
}

// Store the promise resolver
let dialogResolve: ((value: boolean) => void) | null = null;

function handleAddMeteringPoint() {
  formDataStore.addMeteringPoint();
  isNoMeteringPointsDialogVisible.value = false;
  if (dialogResolve) {
    dialogResolve(false); // Don't proceed to next step
    dialogResolve = null;
  }
}

function handleContinueWithoutMeteringPoints() {
  isNoMeteringPointsDialogVisible.value = false;
  if (dialogResolve) {
    dialogResolve(true); // Continue to next step
    dialogResolve = null;
  }
}

async function formValidates() {
  if (!theForm.value) return true;
  const result = await theForm.value.validate();
  const valid = result.valid;

  if (!valid) {
     formValidation.scrollToFirstValidationError({
      nextTick
    });
  }

  return valid;
}

defineExpose({
  formValidates,
  showNoMeteringPointsDialog
});

</script>

<template>
  <div>
     <div class="background-divider"></div>
    <div class="pa-4">
      <p class="textFormHeadline">Was sind Zählpunkte?</p>
      <p class="textDescriptor">
        Zählpunkte stellen die eindeutige Identifizierung der Liefer – oder Bezugsrichtung von Smart Meter dar.
        Jeder Stromanschluss verfügt über einen Bezugszählpunkt. Im Falle von Erzeugungsanlagen kommt ebenso
        ein Einspeisezählpunkt hinzu.
      </p>
      <p class="textDescriptor ml-3">
        • Wenn Sie Verbraucher sind, fügen Sie Ihren Bezugszählpunkt hinzu.
      </p>
      <p class="textDescriptor ml-3">
        • Wenn Sie Energie produzieren, können Sie Ihren Erzeugungszählpunkt (zusätzlich oder alleine) hinzufügen.
      </p>
      <p class="textDescriptor">
        Die Zählpunktnummer können Sie auf Ihrer Strom- und/oder Netzrechnung finden.
        Ebenso im Kundenportal (Webportal) der meisten Netzbetreiber.
      </p>
      <p class="textDescriptor" v-if="multiDsoMode">
        Nachdem Sie die Postleitzahl eingetragen haben, wählen Sie bitte Ihren Netzbetreiber aus.
      </p>
    </div>
    <div class="background-divider"></div>
     <v-form ref="theForm" v-model="formValid">
      <div v-for="(meteringPoint, index) in formDataStore.formData.meteringPointsAssociated" :key="index" class="metering-point-container">
        <div class="pa-4">
        <div class="d-flex justify-space-between align-center mb-3">
          <p class="textFormHeadline" v-if="formDataStore.formData.meteringPointsAssociated.length > 0">Zählpunkte {{index + 1}}</p>
          <div class="remove-button">
              <v-icon color="dark-grey-3" @click="formDataStore.removeMeteringPoint(index)" :disabled="false">mdi-close</v-icon>
          </div>
        </div>
        <MeteringPointDataTable
          :indexNumber="index + 1"
          v-model:meteringPointModel="formDataStore.formData.meteringPointsAssociated[index] as any"
        />
        </div>
        <div v-if="index < formDataStore.formData.meteringPointsAssociated.length - 1" class="background-divider"></div>
      </div>
      <DialogConfirm
        :modelValue="isNoMeteringPointsDialogVisible"
        @update:modelValue="isNoMeteringPointsDialogVisible = $event"
        @confirm="handleAddMeteringPoint"
        @cancel="handleContinueWithoutMeteringPoints"
        :title="t('noMeteringPoints')"
        :message="t('noMeteringPointsMessage')"
        :confirmText="t('add')"
        :cancelText="t('continueWithout')"
      />
    </v-form>
    <div class="background-divider"></div>
  </div>
</template>

<style scoped>
.metering-point-container {
  position: relative;
  background-color: rgb(var(--v-theme-white));
}
</style>

